import Cookies from 'js-cookie';
import { takeEvery, call, put, all, delay } from 'redux-saga/effects';
import braze, { BRAZE_CUSTOM_EVENTS } from 'helpers/braze';
import { setKeyForUser } from 'helpers/local-storage';
import { fetchEntity, fetchEntityIfNeeded } from '@classpass/saga-tools';
import _get from 'lodash/get';
import {
  DISMISSED_PRICING_BANNER_VENUE_IDS_LOCAL_STORAGE_KEY,
  DISMISSED_SMART_SPOT_BANNER_VENUE_IDS_LOCAL_STORAGE_KEY,
} from 'constants/logic';

import { makePath } from 'helpers/link-generation';
import { forceRedirectOutsideReact } from 'helpers/page-utils';
import { consumeNextUrlForLogin } from 'helpers/session-auth-redirect';
import { userInfo, logOut } from 'store/saga/entities/bound';
import { createErrorBanner } from 'components/Banner';
import { closeBrazeSession, waitForNavigate } from 'features/App/App.saga';

import { authSagaMessages as messages } from './Auth.messages';

export const USER_LOGIN = 'USER_LOGIN';
export const USER_FORGOT_PASSWORD = 'USER_FORGOT_PASSWORD';
export const USER_RESET_PASSWORD = 'USER_RESET_PASSWORD';
export const MFA_VERIFICATION_SUCCESS = 'MFA_VERIFICATION_SUCCESS';
export const LOGIN_RATE_LIMIT_FALLBACK_MS = 60000;

export const userLogin = ({ email, password, redirect }, { rejectSubmit }) => ({
  type: USER_LOGIN,
  email,
  password,
  redirect,
  rejectSubmit,
});
export const userForgotPassword = ({ email }, { rejectSubmit, resolveSubmit }) => ({
  type: USER_FORGOT_PASSWORD,
  email,
  rejectSubmit,
  resolveSubmit,
});
export const userResetPassword = ({ password, resetToken }, { rejectSubmit, resolveSubmit }) => ({
  type: USER_RESET_PASSWORD,
  password,
  resetToken,
  rejectSubmit,
  resolveSubmit,
});

export const mfaVerificationSuccess = loginResponse => ({
  type: MFA_VERIFICATION_SUCCESS,
  loginResponse,
});

export function* redirectIfAuthed(client) {
  const user = yield call(fetchEntityIfNeeded, userInfo, [client]);

  if (user.isAuthenticated) {
    yield call(waitForNavigate, client);
    const next = yield call(consumeNextUrlForLogin, user, client.routes);
    yield call(client.navigate, next || '/home');
  }
}

export function* doHandleLoginSuccess(loginResponse) {
  const { userId } = loginResponse;
  let next = yield call([Cookies, Cookies.get], 'next');
  if (next) {
    yield call([Cookies, Cookies.remove], 'next');
  } else {
    next = '/home';
  }
  const url = new URL(next, window.location.origin);

  // Reset dismissed SmartSpot banner venue ids upon successful login
  yield call(setKeyForUser, userId, DISMISSED_SMART_SPOT_BANNER_VENUE_IDS_LOCAL_STORAGE_KEY, []);
  yield call(setKeyForUser, userId, DISMISSED_PRICING_BANNER_VENUE_IDS_LOCAL_STORAGE_KEY, []);
  yield call(forceRedirectOutsideReact, url);
}

function getRetryAfterMs(response) {
  const seconds = Number(_get(response, 'headers.retry-after'));
  return seconds > 0 ? seconds * 1000 : LOGIN_RATE_LIMIT_FALLBACK_MS;
}

function* doHandleMfaSuccess(action) {
  yield doHandleLoginSuccess(action.loginResponse);
}

export function* requestLogin(client, action) {
  const { email, password, rejectSubmit } = action;
  try {
    const loginResponse = yield call(fetchEntity, userInfo, [client, { email, password }]);
    const { requiresMfa } = loginResponse;

    if (requiresMfa) {
      yield call(client.navigate, makePath.loginMFACode(), {
        state: { mfaContinuationToken: loginResponse.mfaContinuationToken },
      });
    } else {
      yield doHandleLoginSuccess(loginResponse);
    }
  } catch (error) {
    if (_get(error, 'response.status') === 429) {
      yield put(createErrorBanner(messages.rateLimited));
      yield delay(getRetryAfterMs(error.response));
      yield call(rejectSubmit);
    } else if (_get(error, 'response.data.data.inactive', false)) {
      yield all([call(rejectSubmit), call(client.navigate, '/inactive-account')]);
    } else {
      yield call(rejectSubmit, messages.invalidCreds);
    }
  }
}

export function* doUserForgotPassword(client, action) {
  const { email, rejectSubmit, resolveSubmit } = action;
  try {
    yield call(fetchEntity, userInfo, [client, { email }]);
    yield call(resolveSubmit);
    yield call(client.navigate, '/reset-password/sent');
  } catch (error) {
    yield call(rejectSubmit);
    yield put(createErrorBanner(messages.processing));
  }
}

export function* doUserResetPassword(client, action) {
  const { password, resetToken, resolveSubmit, rejectSubmit } = action;

  try {
    yield call(fetchEntity, userInfo, [client, { password, resetToken }]);
    yield call(braze.trackEvent, BRAZE_CUSTOM_EVENTS.PASSWORD_CHANGED);
    yield call(resolveSubmit);
    yield call(client.navigate, '/login');
  } catch (error) {
    yield call(rejectSubmit, messages.resetExpired);
  }
}

export function* handleLogoutRouteEnter(client) {
  try {
    yield call(fetchEntity, logOut, [client]);
    yield call(forceRedirectOutsideReact, makePath.login());
    yield call(closeBrazeSession);
  } catch (error) {
    yield call(client.navigate, -1);
    yield put(createErrorBanner(messages.logOut));
  }
}

export function* watchAuthActions(client) {
  yield takeEvery(USER_LOGIN, requestLogin, client);
  yield takeEvery(USER_FORGOT_PASSWORD, doUserForgotPassword, client);
  yield takeEvery(USER_RESET_PASSWORD, doUserResetPassword, client);
  yield takeEvery(MFA_VERIFICATION_SUCCESS, doHandleMfaSuccess);
}
